import { EChartsOption } from 'echarts'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { DateRange } from 'react-day-picker'

import { getSpendMetrics } from '@/api/clarity'
import { mockDataHandler } from '@/api/clarity/mockSpend/mockDatahandler'
import { ScalrTableColumn } from '@/components/customComponents'
import { chartColors } from '@/constants'
import { pieChartOptions } from '@/constants/CHART_CONSTANTS'
import { formatMetric } from '@/hooks/clarity/util'
import {
  CategoryPtiModel,
  CategorySpendModel,
  MetricCardComparisonModel,
  SpendBranchModel,
  SpendCategoryModel,
  SpendDistributorModel,
  SpendMaterialCodeModel,
  SpendMetricsModel,
  SpendMonthlyModel,
  SpendRebatesModel,
  SpendSummaryModel,
  SupplierPtiModel,
  SupplierSpendModel,
} from '@/types/clarity'
import { dateToString } from '@/utils'
import { DateType, getDateRange, getTrendingDate } from '@/utils/date'

const categorySpendLeaderboardColumns: ScalrTableColumn[] = [
  { header: 'Category', accessorKey: 'category', type: 'text' },
  { header: 'Spend', accessorKey: 'totalSpend', type: 'money' },
  { header: '$ Change', accessorKey: 'moneyChange', type: 'money' },
  { header: '% Change', accessorKey: 'percentageChange', type: 'percentage' },
]

const categoryPtiLeaderboardColumns: ScalrTableColumn[] = [
  { header: 'Category', accessorKey: 'category', type: 'text' },
  {
    header: 'PTI Impact',
    accessorKey: 'impact',
    type: 'money',
  },
  {
    header: 'PTI %',
    accessorKey: 'ptiPercentage',
    type: 'percentage',
  },
]

const supplierSpendLeaderboardColumns: ScalrTableColumn[] = [
  { header: 'Supplier', accessorKey: 'supplier', type: 'text' },
  { header: 'Spend', accessorKey: 'totalSpend', type: 'money' },
  { header: '$ Change', accessorKey: 'moneyChange', type: 'money' },
  { header: '% Change', accessorKey: 'percentageChange', type: 'percentage' },
]

const supplierPtiLeaderboardColumns: ScalrTableColumn[] = [
  { header: 'Supplier', accessorKey: 'supplier', type: 'text' },
  {
    header: 'PTI Impact',
    accessorKey: 'impact',
    type: 'money',
  },
  {
    header: 'PTI %',
    accessorKey: 'ptiPercentage',
    type: 'percentage',
  },
]

const rebateTrackingColumns: ScalrTableColumn[] = [
  { header: 'Supplier', accessorKey: 'vendorName', type: 'text' },
  { header: 'Rebates', accessorKey: 'totalRebate', type: 'money' },
  { header: 'Spend', accessorKey: 'totalSpend', type: 'money' },
]

export default function useSpendMetrics(
  startDate: string,
  endDate: string,
  dateFilterType: DateType,
  branches: string,
  tenant: string
) {
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)
  const [spendMetrics, setSpendMetrics] = useState<SpendMetricsModel>()
  const [spendMetricsTrending, setSpendMetricsTrending] =
    useState<SpendMetricsModel>()
  const [metricCardData, setMetricCardData] =
    useState<MetricCardComparisonModel<SpendMetricsModel>>()
  const [categoryPtiData, setCategoryPtiData] = useState<CategoryPtiModel[]>([])
  const [categorySpendData, setCategorySpendData] = useState<
    CategorySpendModel[]
  >([])
  const [supplierPtiData, setSupplierPtiData] = useState<SupplierPtiModel[]>([])
  const [supplierSpendData, setSupplierSpendData] = useState<
    SupplierSpendModel[]
  >([])
  const [categoryPieChartData, setCategoryPieChartData] =
    useState<EChartsOption>()
  const [supplierPieChartData, setSupplierPieChartData] =
    useState<EChartsOption>()
  const [materialCodeDotChartOptions, setMaterialCodeDotChartOptions] =
    useState<EChartsOption>()
  const [rebateTrackingData, setRebateTrackingData] = useState<
    SpendRebatesModel[]
  >([])

  const fetchData = useMemo(() => {
    return async () => {
      try {
        const { spendMetrics } = await fetchSpendMetrics(
          branches,
          startDate,
          endDate,
          tenant
        )
        setLocalTrendingData(branches, setMetricCardData, tenant)

        const { categoryPieChartData, supplierPieChartData } =
          getPieChartData(spendMetrics)

        const materialCodeDotChartOptions = getMaterialCodeDotChartOptions(
          spendMetrics.materialCode
        )

        setSpendMetrics(spendMetrics)
        setCategoryPieChartData(categoryPieChartData)
        setSupplierPieChartData(supplierPieChartData)
        setMaterialCodeDotChartOptions(materialCodeDotChartOptions)
        setRebateTrackingData(spendMetrics.rebates)
      } catch (err) {
        setError(
          new Error(
            err instanceof Error ? err.message : 'An unknown error occurred'
          )
        )
      }
    }
  }, [branches.toString(), startDate, endDate])

  const fetchTrendData = useMemo(() => {
    return async () => {
      try {
        const trendingDate = getTrendingDate(
          { from: new Date(startDate), to: new Date(endDate) },
          dateFilterType
        ) as DateRange

        const { spendMetrics } = await fetchSpendMetrics(
          branches,
          dateToString(trendingDate, 'from', 'yyyy-mm'),
          dateToString(trendingDate, 'to', 'yyyy-mm'),
          tenant
        )

        setSpendMetricsTrending(spendMetrics)
      } catch (err) {
        setError(
          new Error(
            err instanceof Error ? err.message : 'An unknown error occurred'
          )
        )
      }
    }
  }, [branches.toString(), startDate, endDate, dateFilterType])

  const fetchAllData = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      await Promise.all([fetchData(), fetchTrendData()])
    } catch (err) {
      setError(
        new Error(
          err instanceof Error ? err.message : 'An unknown error occurred'
        )
      )
    } finally {
      setLoading(false)
    }
  }, [fetchData, fetchTrendData])

  const refetch = useCallback(() => {
    console.log('Refetching spend metrics data...')
    return fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    if (spendMetrics && spendMetricsTrending) {
      const { categoryPtiData, categorySpendData } = getCategoryData(
        spendMetrics,
        spendMetricsTrending
      )

      const { supplierPtiData, supplierSpendData } = getSupplierData(
        spendMetrics,
        spendMetricsTrending
      )

      setCategoryPtiData(categoryPtiData)
      setCategorySpendData(categorySpendData)
      setSupplierPtiData(supplierPtiData)
      setSupplierSpendData(supplierSpendData)
    }
  }, [spendMetrics, spendMetricsTrending])

  return {
    spendMetrics,
    spendMetricsTrending,
    metricCardData,
    categoryPtiData,
    categorySpendData,
    supplierPtiData,
    supplierSpendData,
    categoryPtiLeaderboardColumns,
    categorySpendLeaderboardColumns,
    supplierPtiLeaderboardColumns,
    supplierSpendLeaderboardColumns,
    categoryPieChartData,
    supplierPieChartData,
    materialCodeDotChartOptions,
    rebateTrackingColumns,
    rebateTrackingData,
    loading,
    error,
    refetch,
  }
}

async function setLocalTrendingData(
  branches: string,
  setMetricCardData: (
    data: MetricCardComparisonModel<SpendMetricsModel>
  ) => void,
  tenant: string
) {
  const mtdDate = getDateRange('monthly')
  const ytdDate = getDateRange('yearly')

  const mtdTrendingDate = mtdDate.trending as DateRange
  const ytdTrendingDate = ytdDate.trending as DateRange

  const { spendMetrics: qSpendMetrics } = await fetchSpendMetrics(
    branches,
    dateToString(mtdDate, 'from', 'yyyy-mm'),
    dateToString(mtdDate, 'to', 'yyyy-mm'),
    tenant
  )

  const { spendMetrics: qSpendMetricsTrending } = await fetchSpendMetrics(
    branches,
    dateToString(mtdTrendingDate, 'from', 'yyyy-mm'),
    dateToString(mtdTrendingDate, 'to', 'yyyy-mm'),
    tenant
  )

  const { spendMetrics: ySpendMetrics } = await fetchSpendMetrics(
    branches,
    dateToString(ytdDate, 'from', 'yyyy-mm'),
    dateToString(ytdDate, 'to', 'yyyy-mm'),
    tenant
  )
  const { spendMetrics: ySpendMetricsTrending } = await fetchSpendMetrics(
    branches,
    dateToString(ytdTrendingDate, 'from', 'yyyy-mm'),
    dateToString(ytdTrendingDate, 'to', 'yyyy-mm'),
    tenant
  )

  setMetricCardData({
    currMetricsMonthly: qSpendMetrics,
    currMetricsYearly: ySpendMetrics,
    prevMetricsMonthly: qSpendMetricsTrending,
    prevMetricsYearly: ySpendMetricsTrending,
  })
}

async function fetchSpendMetrics(
  branches: string,
  startDate: string,
  endDate: string,
  tenant: string
): Promise<{
  spendMetrics: SpendMetricsModel
}> {
  try {
    const response = await getSpendMetrics(branches, startDate, endDate)

    const mockResponse = await mockDataHandler(branches, startDate, endDate)

    const summaryData: SpendSummaryModel = setSummaryMetric(
      response.summary.total_spend,
      response.summary.unique_invoices,
      response.summary.compliant_count,
      response.summary.non_compliant_count,
      response.summary.compliant_spend,
      response.summary.spend_compliance_percentage,
      response.summary.price_trend_spend,
      response.summary.current_spend,
      response.summary.impact,
      response.summary.pti_percentage,
      response.summary.cogs,
      response.summary.cogs_percentage,
      mockResponse.cogsVsTheoretical[0].cogsDifference,
      mockResponse.inventoryByLocation[0].inventoryValue
    )

    const monthlyData: SpendMonthlyModel[] = response.monthly_data.map(
      (responseMetrics: any) => {
        return setMonthlyMetric(
          responseMetrics.year_month,
          responseMetrics.total_spend,
          responseMetrics.unique_invoices,
          responseMetrics.compliant_count,
          responseMetrics.non_compliant_count,
          responseMetrics.compliant_spend,
          responseMetrics.spend_compliance_percentage,
          responseMetrics.price_trend_spend,
          responseMetrics.current_spend,
          responseMetrics.impact,
          responseMetrics.pti_percentage
        )
      }
    )

    const branchData: SpendBranchModel[] = response.branch_data.map(
      (responseMetrics: any) => {
        return setBranchMetric(
          responseMetrics.dcn,
          responseMetrics.total_spend,
          responseMetrics.unique_invoices,
          responseMetrics.compliant_count,
          responseMetrics.non_compliant_count,
          responseMetrics.compliant_spend,
          responseMetrics.spend_compliance_percentage,
          responseMetrics.price_trend_spend,
          responseMetrics.current_spend,
          responseMetrics.impact,
          responseMetrics.pti_percentage
        )
      }
    )

    const distributorData: SpendDistributorModel[] = response.distributors.map(
      (responseMetrics: any) => {
        return setDistributorMetric(
          responseMetrics.name,
          responseMetrics.count,
          responseMetrics.spend,
          responseMetrics.price_trend_spend,
          responseMetrics.current_spend,
          responseMetrics.impact,
          responseMetrics.pti_percentage,
          responseMetrics.compliance_percentage,
          responseMetrics.spend_compliance_percentage,
          summaryData.totalSpend
        )
      }
    )

    const categoriesData: SpendCategoryModel[] = response.categories.map(
      (responseMetrics: any) => {
        return setCategoryMetric(
          responseMetrics.name,
          responseMetrics.count,
          responseMetrics.spend,
          responseMetrics.price_trend_spend,
          responseMetrics.current_spend,
          responseMetrics.impact,
          responseMetrics.pti_percentage,
          responseMetrics.compliance_percentage,
          responseMetrics.spend_compliance_percentage,
          summaryData.totalSpend
        )
      }
    )

    let materialCodeData: SpendMaterialCodeModel[] = []
    let rebatesData: SpendRebatesModel[] = []
    if (tenant === 'blankstreet') {
      materialCodeData = response.material_code.map((responseMetrics: any) => {
        return setMaterialCodeMetric(
          responseMetrics.material_code,
          responseMetrics.pti_percentage,
          responseMetrics.impact
        )
      })

      rebatesData = response.rebates.map((responseMetrics: any) => {
        return setRebatesMetric(
          responseMetrics.vendor_name,
          responseMetrics.total_rebate,
          responseMetrics.total_spend
        )
      })
    }

    return {
      spendMetrics: {
        summary: summaryData,
        branchData,
        monthlyData,
        distributors: distributorData,
        categories: categoriesData,
        materialCode: materialCodeData,
        rebates: rebatesData,
      },
    }
  } catch (err) {
    throw new Error(
      err instanceof Error ? err.message : 'An unknown error occurred'
    )
  }
}

function setSummaryMetric(
  totalSpend: number,
  uniqueInvoices: number,
  compliantCount: number,
  nonCompliantCount: number,
  compliantSpend: number,
  spendCompliancePercentage: number,
  priceTrendSpend: number,
  currentSpend: number,
  impact: number,
  ptiPercentage: number,
  cogs: number,
  cogsPercentage: number,
  cogsVsTheoretical: number,
  inventoryValue: number
): SpendSummaryModel {
  return {
    totalSpend: formatMetric(totalSpend, 0),
    uniqueInvoices: formatMetric(uniqueInvoices, 0),
    compliantCount: formatMetric(compliantCount, 0),
    nonCompliantCount: formatMetric(nonCompliantCount, 0),
    compliantSpend: formatMetric(compliantSpend, 0),
    spendCompliancePercentage: formatMetric(spendCompliancePercentage, 2),
    priceTrendSpend: formatMetric(priceTrendSpend, 0),
    currentSpend: formatMetric(currentSpend, 0),
    impact: formatMetric(impact, 2),
    ptiPercentage: formatMetric(ptiPercentage, 2),
    cogs: formatMetric(cogs, 2),
    cogsPercentage: formatMetric(cogsPercentage, 2),
    cogsVsTheoretical: formatMetric(cogsVsTheoretical, 2),
    inventoryValue: formatMetric(inventoryValue, 0),
  }
}

function setMonthlyMetric(
  yearMonth: string,
  totalSpend: number,
  uniqueInvoices: number,
  compliantCount: number,
  nonCompliantCount: number,
  compliantSpend: number,
  spendCompliancePercentage: number,
  priceTrendSpend: number,
  currentSpend: number,
  impact: number,
  ptiPercentage: number
): SpendMonthlyModel {
  return {
    yearMonth,
    totalSpend: formatMetric(totalSpend, 0),
    uniqueInvoices: formatMetric(uniqueInvoices, 0),
    compliantCount: formatMetric(compliantCount, 0),
    nonCompliantCount: formatMetric(nonCompliantCount, 0),
    compliantSpend: formatMetric(compliantSpend, 0),
    spendCompliancePercentage: formatMetric(spendCompliancePercentage, 2),
    priceTrendSpend: formatMetric(priceTrendSpend, 0),
    currentSpend: formatMetric(currentSpend, 0),
    impact: formatMetric(impact, 2),
    ptiPercentage: formatMetric(ptiPercentage, 2),
  }
}

function setBranchMetric(
  dcn: string,
  totalSpend: number,
  uniqueInvoices: number,
  compliantCount: number,
  nonCompliantCount: number,
  compliantSpend: number,
  spendCompliancePercentage: number,
  priceTrendSpend: number,
  currentSpend: number,
  impact: number,
  ptiPercentage: number
): SpendBranchModel {
  return {
    dcn,
    totalSpend: formatMetric(totalSpend, 0),
    uniqueInvoices: formatMetric(uniqueInvoices, 0),
    compliantCount: formatMetric(compliantCount, 0),
    nonCompliantCount: formatMetric(nonCompliantCount, 0),
    compliantSpend: formatMetric(compliantSpend, 0),
    spendCompliancePercentage: formatMetric(spendCompliancePercentage, 2),
    priceTrendSpend: formatMetric(priceTrendSpend, 0),
    currentSpend: formatMetric(currentSpend, 0),
    impact: formatMetric(impact, 2),
    ptiPercentage: formatMetric(ptiPercentage, 2),
  }
}

function setDistributorMetric(
  name: string,
  count: number,
  spend: number,
  priceTrendSpend: number,
  currentSpend: number,
  impact: number,
  ptiPercentage: number,
  compliancePercentage: number,
  spendCompliancePercentage: number,
  percentageOfTotalSpend: number
): SpendDistributorModel {
  const pOfTotalSpend = (Number(spend) / Number(percentageOfTotalSpend)) * 100

  return {
    name,
    count: formatMetric(count, 0),
    spend: formatMetric(spend, 0),
    priceTrendSpend: formatMetric(priceTrendSpend, 0),
    currentSpend: formatMetric(currentSpend, 0),
    impact: formatMetric(impact, 2),
    ptiPercentage: formatMetric(ptiPercentage, 2),
    compliancePercentage: formatMetric(compliancePercentage, 2),
    spendCompliancePercentage: formatMetric(spendCompliancePercentage, 2),
    percentageOfTotalSpend: formatMetric(pOfTotalSpend, 2),
  }
}

function setCategoryMetric(
  name: string,
  count: number,
  spend: number,
  priceTrendSpend: number,
  currentSpend: number,
  impact: number,
  ptiPercentage: number,
  compliancePercentage: number,
  spendCompliancePercentage: number,
  percentageOfTotalSpend: number
): SpendCategoryModel {
  const pOfTotalSpend = (Number(spend) / Number(percentageOfTotalSpend)) * 100

  return {
    name: name,
    count: formatMetric(count, 0),
    spend: formatMetric(spend, 0),
    priceTrendSpend: formatMetric(priceTrendSpend, 0),
    currentSpend: formatMetric(currentSpend, 0),
    impact: formatMetric(impact, 2),
    ptiPercentage: formatMetric(ptiPercentage, 2),
    compliancePercentage: formatMetric(compliancePercentage, 2),
    spendCompliancePercentage: formatMetric(spendCompliancePercentage, 2),
    percentageOfTotalSpend: formatMetric(pOfTotalSpend, 2),
  }
}

function setMaterialCodeMetric(
  materialCode: string,
  ptiPercentage: number,
  impact: number
): SpendMaterialCodeModel {
  return {
    materialCode,
    ptiPercentage: formatMetric(ptiPercentage, 2),
    impact: formatMetric(impact, 2),
  }
}

function setRebatesMetric(
  vendorName: string,
  totalRebate: number,
  totalSpend: number
): SpendRebatesModel {
  return {
    vendorName,
    totalRebate: formatMetric(totalRebate, 0),
    totalSpend: formatMetric(totalSpend, 0),
  }
}

function getCategoryData(
  spendMetrics: SpendMetricsModel,
  spendMetricsTrending: SpendMetricsModel
): {
  categoryPtiData: CategoryPtiModel[]
  categorySpendData: CategorySpendModel[]
} {
  const categoryPtiMetrics = spendMetrics.categories
    .sort((a, b) => b.impact - a.impact)
    .slice(0, 5)

  const categoryPtiData: CategoryPtiModel[] = []
  let categorySpendData: CategorySpendModel[] = []

  categoryPtiMetrics.forEach((metric) => {
    const data: CategoryPtiModel = {
      category: metric.name,
      impact: metric.impact,
      ptiPercentage: metric.ptiPercentage,
    }
    categoryPtiData.push(data)
  })

  spendMetrics?.categories.forEach((metric) => {
    const trendingMetric = spendMetricsTrending.categories.find(
      (trending) => trending.name === metric.name
    )

    const moneyChange = trendingMetric
      ? metric.spend - trendingMetric?.spend
      : 0

    const percentageChange = trendingMetric
      ? (moneyChange / trendingMetric.spend) * 100
      : 0

    const data: CategorySpendModel = {
      category: metric.name,
      totalSpend: metric.spend,
      moneyChange: formatMetric(moneyChange, 0),
      percentageChange: formatMetric(percentageChange, 2),
    }
    categorySpendData.push(data)
  })

  categorySpendData = categorySpendData
    .sort((a, b) => b.moneyChange - a.moneyChange)
    .slice(0, 5)

  return { categoryPtiData, categorySpendData }
}

function getSupplierData(
  spendMetrics: SpendMetricsModel,
  spendMetricsTrending: SpendMetricsModel
): {
  supplierPtiData: SupplierPtiModel[]
  supplierSpendData: SupplierSpendModel[]
} {
  const supplierPtiMetrics = spendMetrics.distributors
    .sort((a, b) => b.impact - a.impact)
    .slice(0, 5)

  const supplierPtiData: SupplierPtiModel[] = []
  let supplierSpendData: SupplierSpendModel[] = []

  supplierPtiMetrics.forEach((metric) => {
    const data: SupplierPtiModel = {
      supplier: metric.name,
      impact: metric.impact,
      ptiPercentage: metric.ptiPercentage,
    }
    supplierPtiData.push(data)
  })

  spendMetrics?.distributors.forEach((metric) => {
    const trendingMetric = spendMetricsTrending.distributors.find(
      (trending) => trending.name === metric.name
    )

    const moneyChange = trendingMetric
      ? metric.spend - trendingMetric?.spend
      : 0

    const percentageChange = trendingMetric
      ? (moneyChange / trendingMetric.spend) * 100
      : 0

    const data: SupplierSpendModel = {
      supplier: metric.name,
      totalSpend: metric.spend,
      moneyChange: formatMetric(moneyChange, 0),
      percentageChange: formatMetric(percentageChange, 2),
    }
    supplierSpendData.push(data)
  })

  supplierSpendData = supplierSpendData
    .sort((a, b) => b.moneyChange - a.moneyChange)
    .slice(0, 5)

  return { supplierPtiData, supplierSpendData }
}

function getPieChartData(spendMetrics: SpendMetricsModel): {
  categoryPieChartData: EChartsOption
  supplierPieChartData: EChartsOption
} {
  const categoryPieChartData = pieChartOptions(
    spendMetrics.categories.map((category) => category.count),
    spendMetrics.categories.map((category) => category.name)
  )

  const supplierPieChartData = pieChartOptions(
    spendMetrics.distributors.map((supplier) => supplier.count),
    spendMetrics.distributors.map((supplier) => supplier.name)
  )

  return { categoryPieChartData, supplierPieChartData }
}

const getMaterialCodeDotChartOptions = (
  materialCodePricingDeviationRawData: SpendMaterialCodeModel[]
): EChartsOption => {
  const sortedData = materialCodePricingDeviationRawData.sort(
    (a, b) => b.impact - a.impact
  )
  const impactData = sortedData.map((item) => item.impact)
  const ptiPercentageData = sortedData.map((item) => item.ptiPercentage)
  const materialCodeNames = sortedData.map((item) => item.materialCode)

  return {
    tooltip: {
      trigger: 'axis',
      formatter: function (params: any) {
        const index = params[0].dataIndex
        return `${materialCodeNames[index]}<br/>
                ${params[0].marker}${params[0].seriesName}: $${impactData[index].toLocaleString()}<br/>
                % From Plan: ${ptiPercentageData[index]}%`
      },
    },
    grid: {
      bottom: '20%',
      left: '12%',
      right: '2%',
    },
    legend: {
      selectedMode: false,
      orient: 'horizontal',
      top: '0%',
      left: 'center',
      data: [
        {
          name: 'Loss Impact',
          itemStyle: {
            color: chartColors[0],
          },
        },
      ],
      icon: 'circle',
      itemGap: 20,
    },
    xAxis: [
      {
        type: 'category',
        data: materialCodeNames,
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          interval: 0,
          formatter: function (value: string) {
            return value.split(' ').join('\n')
          },
        },
      },
    ],
    yAxis: [
      {
        type: 'value',
        name: 'Loss (Impact)',
        axisLabel: {
          formatter: '${value}',
        },
      },
    ],
    series: [
      {
        name: 'Loss Impact',
        type: 'bar',
        barWidth: '60%',
        data: impactData.map((value, index) => ({
          value: value,
          itemStyle: {
            color: chartColors[0],
            borderRadius: 8,
            borderWidth: 1,
            borderColor: '#fff',
          },
          label: {
            show: true,
            position: 'top',
            formatter: function () {
              return ptiPercentageData[index] + '%'
            },
            fontSize: 12,
            color: '#666',
            backgroundColor: '#fff',
            padding: [4, 8],
            borderRadius: 4,
            borderWidth: 1,
            borderColor: '#ddd',
          },
        })),
      },
    ],
  }
}
